import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import {
  MessageOutlined,
  ReloadOutlined,
  RiseOutlined,
  ShopOutlined,
  StarOutlined,
} from '@ant-design/icons';
import PageShell from '../components/Layout/PageShell';
import { vendorAPI } from '../services/api';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchStores } from '../store/slices/storesSlice';
import type { AnalyticsData, ApiResponse, ListingImage } from '../types';

const { Text } = Typography;

const PIE_COLORS = ['#0171BF', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#64748b'];

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function mergeKey(id: { year: number; month: number; day: number }) {
  return `${id.year}-${pad2(id.month)}-${pad2(id.day)}`;
}

function leadSourceLabel(raw: string | null): string {
  if (raw == null || raw === '') return 'Unknown';
  const s = String(raw).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatPrice(amount: number, currency?: string) {
  const c = currency || 'INR';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(
      amount
    );
  } catch {
    return `${c} ${amount}`;
  }
}

function firstListingImage(images: Array<string | ListingImage> | undefined): string | undefined {
  if (!images?.length) return undefined;
  const x = images[0];
  if (typeof x === 'string') return x;
  return x?.url;
}

type Period = '7d' | '30d' | '90d';

const Analytics: React.FC = () => {
  const dispatch = useAppDispatch();
  const { stores } = useAppSelector((s) => s.stores);
  const [period, setPeriod] = useState<Period>('30d');
  const [storeFilter, setStoreFilter] = useState('');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchStores({ page: 1, limit: 200 }));
  }, [dispatch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await vendorAPI.getAnalytics({
        period,
        ...(storeFilter ? { store: storeFilter } : {}),
      });
      const body = res.data as ApiResponse<AnalyticsData>;
      setData(body.data ?? null);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(msg || 'Could not load analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, storeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const storeOptions = useMemo(
    () => [...stores.map((s) => ({ value: s._id, label: s.storeName }))],
    [stores]
  );

  const activityChart = useMemo(() => {
    if (!data) return [];
    const byKey = new Map<string, { label: string; leads: number; listings: number }>();

    data.trends.leads.forEach((t) => {
      const k = mergeKey(t._id);
      const d = new Date(t._id.year, t._id.month - 1, t._id.day);
      const ex = byKey.get(k) || { label: dayjs(d).format('MMM D'), leads: 0, listings: 0 };
      ex.leads = t.count;
      byKey.set(k, ex);
    });
    data.trends.listings.forEach((t) => {
      const k = mergeKey(t._id);
      const d = new Date(t._id.year, t._id.month - 1, t._id.day);
      const ex = byKey.get(k) || { label: dayjs(d).format('MMM D'), leads: 0, listings: 0 };
      ex.listings = t.count;
      byKey.set(k, ex);
    });

    return [...byKey.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [data]);

  const reviewChart = useMemo(() => {
    if (!data) return [];
    return [...data.trends.reviews]
      .map((t) => {
        const d = new Date(t._id.year, t._id.month - 1, t._id.day);
        return {
          sortKey: mergeKey(t._id),
          label: dayjs(d).format('MMM D'),
          count: t.count,
          avgRating: t.avgRating != null ? Math.round(t.avgRating * 10) / 10 : 0,
        };
      })
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [data]);

  const leadSourcesPie = useMemo(() => {
    if (!data?.leadSources.length) return [];
    return data.leadSources.map((s) => ({
      name: leadSourceLabel(s._id),
      value: s.count,
    }));
  }, [data]);

  const summary = data?.summary;
  const fallbackLeads = data ? data.trends.leads.reduce((a, t) => a + t.count, 0) : 0;
  const fallbackListings = data ? data.trends.listings.reduce((a, t) => a + t.count, 0) : 0;
  const fallbackReviews = data ? data.trends.reviews.reduce((a, t) => a + t.count, 0) : 0;

  const kpiLeads = summary?.leads ?? fallbackLeads;
  const kpiListings = summary?.newListings ?? fallbackListings;
  const kpiReviews = summary?.reviews ?? fallbackReviews;
  const kpiAvgRating = summary?.avgRating ?? 0;
  const kpiResponseHrs = summary?.avgResponseHours ?? 0;

  const periodLabel =
    period === '7d' ? 'Last 7 days' : period === '90d' ? 'Last 90 days' : 'Last 30 days';

  const topColumns: ColumnsType<AnalyticsData['topListings'][0]> = [
    {
      title: 'Listing',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (t: string, row) => (
        <div className="flex min-w-0 items-center gap-2">
          {firstListingImage(row.images) ? (
            <img src={firstListingImage(row.images)} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="h-9 w-9 shrink-0 rounded-lg bg-slate-100" />
          )}
          <span className="truncate font-medium text-slate-900">{t}</span>
        </div>
      ),
    },
    {
      title: 'Leads',
      dataIndex: 'leadCount',
      key: 'leadCount',
      width: 90,
      align: 'right',
      render: (n: number) => <span className="tabular-nums">{n}</span>,
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      width: 90,
      align: 'right',
      render: (n: number) => <span className="tabular-nums">{n ?? 0}</span>,
    },
    {
      title: 'Price',
      key: 'price',
      width: 120,
      align: 'right',
      render: (_, row) => (
        <span className="tabular-nums text-slate-700">{formatPrice(row.price, row.currency)}</span>
      ),
    },
  ];

  const showSkeleton = loading && !data;

  const kpis = [
    {
      key: 'leads',
      label: 'Leads',
      value: kpiLeads,
      sub: 'Buyer enquiries in range',
      icon: <MessageOutlined />,
      tone: 'text-[#0171BF]',
      bg: 'bg-[#0171BF]',
    },
    {
      key: 'listings',
      label: 'New listings',
      value: kpiListings,
      sub: 'Created in range',
      icon: <RiseOutlined />,
      tone: 'text-emerald-600',
      bg: 'bg-emerald-500',
    },
    {
      key: 'reviews',
      label: 'Reviews',
      value: kpiReviews,
      sub: 'Posted in range',
      icon: <StarOutlined />,
      tone: 'text-amber-600',
      bg: 'bg-amber-500',
    },
    {
      key: 'rating',
      label: 'Avg. rating',
      value: kpiReviews > 0 ? kpiAvgRating.toFixed(1) : '—',
      sub: 'In range, when reviews exist',
      icon: <StarOutlined />,
      tone: 'text-violet-600',
      bg: 'bg-violet-500',
    },
    {
      key: 'response',
      label: 'Avg. response',
      value: kpiResponseHrs > 0 ? `${kpiResponseHrs} h` : '—',
      sub: 'Time to first reply (leads)',
      icon: <MessageOutlined />,
      tone: 'text-slate-700',
      bg: 'bg-slate-600',
    },
  ];

  return (
    <PageShell
      title="Analytics"
      description={`${periodLabel} · Leads, listings, and buyer feedback across your selected store filter.`}
      className="dashboard-surface"
      titleEnd={
        <Space wrap className="w-full min-[480px]:w-auto min-[480px]:justify-end" size="middle">
          <Select<Period>
            value={period}
            className="min-w-[8.5rem]"
            options={[
              { value: '7d', label: '7 days' },
              { value: '30d', label: '30 days' },
              { value: '90d', label: '90 days' },
            ]}
            onChange={(v) => setPeriod(v)}
          />
          <Select
            className="min-w-[10rem]"
            value={storeFilter === '' ? undefined : storeFilter}
            options={storeOptions}
            placeholder="All stores"
            suffixIcon={<ShopOutlined />}
            onChange={(v) => setStoreFilter(v ?? '')}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading} className="rounded-xl">
            Refresh
          </Button>
        </Space>
      }
    >
      {error ? (
        <Alert type="error" showIcon message={error} className="mb-4 rounded-2xl" />
      ) : null}

      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {kpis.map((kpi) => (
            <Card key={kpi.key} className="rounded-2xl border-slate-200 shadow-sm" styles={{ body: { padding: '1.1rem' } }}>
              {showSkeleton ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-sm ${kpi.bg}`}
                    >
                      {kpi.icon}
                    </span>
                  </div>
                  <p className={`mt-2 text-2xl font-semibold tabular-nums sm:text-3xl ${kpi.tone}`}>{kpi.value}</p>
                  <p className="mt-1 text-xs text-slate-600 sm:text-sm">{kpi.sub}</p>
                </>
              )}
            </Card>
          ))}
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card
              className="h-full rounded-2xl border-slate-200 shadow-sm"
              title={<span className="font-semibold text-slate-900">Leads and new listings</span>}
            >
              <Text type="secondary" className="-mt-1 mb-3 block text-sm">
                Daily counts in the selected period{storeFilter ? ' for this store' : ''}.
              </Text>
              {showSkeleton ? (
                <Skeleton.Node active className="!h-64 !w-full rounded-xl" />
              ) : activityChart.length === 0 ? (
                <Empty description="No activity in this range yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div className="h-64 w-full min-w-0 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={activityChart} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: 12, borderColor: '#e2e8f0' }}
                        labelStyle={{ fontWeight: 600 }}
                      />
                      <Legend />
                      <Bar dataKey="leads" name="Leads" fill="#0171BF" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="listings" name="New listings" fill="#10b981" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card
              className="h-full rounded-2xl border-slate-200 shadow-sm"
              title={<span className="font-semibold text-slate-900">Lead sources</span>}
            >
              <Text type="secondary" className="-mt-1 mb-3 block text-sm">
                Where enquiries originated in this period.
              </Text>
              {showSkeleton ? (
                <Skeleton.Node active className="!h-64 !w-full rounded-xl" />
              ) : leadSourcesPie.length === 0 ? (
                <Empty description="No lead source data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div className="h-64 w-full min-w-0 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={leadSourcesPie}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={88}
                        paddingAngle={2}
                        label={({ name, percent }) => `${name} (${((percent as number) * 100).toFixed(0)}%)`}
                      >
                        {leadSourcesPie.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card
              className="rounded-2xl border-slate-200 shadow-sm"
              title={<span className="font-semibold text-slate-900">Reviews trend</span>}
            >
              <Text type="secondary" className="-mt-1 mb-3 block text-sm">
                Review volume and average stars by day.
              </Text>
              {showSkeleton ? (
                <Skeleton.Node active className="!h-64 !w-full rounded-xl" />
              ) : reviewChart.length === 0 ? (
                <Empty description="No reviews in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div className="h-64 w-full min-w-0 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={reviewChart} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 5]}
                        tick={{ fontSize: 11 }}
                        stroke="#94a3b8"
                      />
                      <Tooltip contentStyle={{ borderRadius: 12, borderColor: '#e2e8f0' }} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="count" name="Reviews" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="avgRating"
                        name="Avg. rating"
                        stroke="#0171BF"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card
              className="rounded-2xl border-slate-200 shadow-sm"
              title={<span className="font-semibold text-slate-900">Response time</span>}
            >
              <Text type="secondary" className="-mt-1 mb-3 block text-sm">
                Average hours until you replied to a lead (when responses exist).
              </Text>
              {showSkeleton ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : kpiResponseHrs <= 0 ? (
                <Empty description="No responses recorded in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                  <p className="text-4xl font-semibold tabular-nums text-[#0171BF]">{kpiResponseHrs}</p>
                  <p className="mt-1 text-sm text-slate-600">hours average</p>
                  <Tag color="processing" className="mt-4">
                    Faster replies often convert better
                  </Tag>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Card
          className="rounded-2xl border-slate-200 shadow-sm"
          title={<span className="font-semibold text-slate-900">Top listings by leads</span>}
        >
          <Text type="secondary" className="-mt-1 mb-3 block text-sm">
            All-time for your selected stores (not limited to the date range).
          </Text>
          {showSkeleton ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : !data?.topListings.length ? (
            <Empty description="No listings yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              rowKey="_id"
              columns={topColumns}
              dataSource={data.topListings}
              pagination={false}
              size="middle"
              scroll={{ x: 520 }}
            />
          )}
        </Card>
      </div>
    </PageShell>
  );
};

export default Analytics;
